import { campaignsApi } from "@/api/campaigns.api";
import { Campaign, CampaignContext, CampaignCreation } from "@/types/campaign";
import { useMutation, useQuery, useQueryClient } from "@tanstack/vue-query";
import { queryClient } from "./queryClient";
import { storeToRefs } from "pinia";
import { invitationApi } from "@/api/invitation.api";

export function useCampaigns() {
  const appStore = storeToRefs(useAppStore());
  return useQuery({
    queryKey: ['campaigns', { organisationId: appStore.currentOrganisationId, spaceId: appStore.currentSpaceId }],
    queryFn: async () => {
      const campaigns = await campaignsApi.getAll(toValue(appStore.currentOrganisationId), toValue(appStore.currentSpaceId));
      campaigns.forEach((c) => {
        queryClient.setQueryData(['campaigns', { organisationId: appStore.currentOrganisationId, spaceId: appStore.currentSpaceId, campaignId: c.id }], c);
      });
      return campaigns;
    },
    enabled: !!toValue(appStore.currentOrganisationId) && !!toValue(appStore.currentSpaceId)
  });
}

export function useCampaign(isOnboarded = false) {
  const appStore = storeToRefs(useAppStore());
  return useQuery({
    queryKey: ['campaigns', { organisationId: appStore.currentOrganisationId, spaceId: appStore.currentSpaceId, campaignId: appStore.currentCampaignId }],
    queryFn: () => campaignsApi.get({ organisationId: toValue(appStore.currentOrganisationId), spaceId: toValue(appStore.currentSpaceId), campaignId: toValue(appStore.currentCampaignId)}, isOnboarded),
    enabled: !!toValue(appStore.currentCampaignId) && !!toValue(appStore.currentSpaceId) && !!toValue(appStore.currentOrganisationId),
  });
}

export function useArchivedCampaigns() {
  const appStore = storeToRefs(useAppStore());
  return useQuery({
    queryKey: ['archived-campaigns', appStore.currentOrganisationId],
    queryFn: () => campaignsApi.getArchivedCampaigns(toValue(appStore.currentOrganisationId)),
    enabled: !!toValue(appStore.currentOrganisationId)
  })
}

export function useCreateCampaign() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { organisationId: string, newCampaign: CampaignCreation }) => campaignsApi.add(data.organisationId, data.newCampaign),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['campaigns', { organisationId: variables.organisationId, spaceId: data.campaign.parent_workspace_id } ] });
    }
  });
}

export function useEditCampaign() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { context: CampaignContext, campaign: Partial<Campaign>, auto_activate: boolean }) => campaignsApi.edit(data.context, data.campaign, data.auto_activate),
    onSuccess: (data, variables: { context: CampaignContext, campaign: Partial<Campaign>, auto_activate: boolean } ) => {
      queryClient.invalidateQueries({ queryKey: ['campaigns', variables.context] });
    }
  });
}

export function useArchiveCampaign() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { organisationId: string, spaceId: string, campaignId: string }) => campaignsApi.archive(data.organisationId, data.spaceId, data.campaignId),
    onSuccess: (data, variables: { organisationId: string, spaceId: string, campaignId: string }) => {
      queryClient.invalidateQueries({ queryKey: ['campaigns', { organisationId: variables.organisationId, campaignId: variables.campaignId }] });
      queryClient.invalidateQueries({ queryKey: ['archivedCampaigns'] });
    },
  });
}

export function useRestoreCampaign() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { context: { organisationId: string, campaignId: string }, spaceId: string }) => campaignsApi.restore(data.context.organisationId, data.context.campaignId, data.spaceId),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['campaigns', { organisationId: variables.context.organisationId, spaceId: variables.spaceId }] });
      queryClient.invalidateQueries({ queryKey: ['archivedCampaigns'] });
    },
  });
}

export function useTogglePublicInvitation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { context: CampaignContext, enabled: boolean }) => campaignsApi.toggleInvitation(data.context, data.enabled),
    onSuccess: (data, variables: { context: CampaignContext, spaceId: string, enabled: boolean }) => {
      queryClient.invalidateQueries({ queryKey: ['campaigns', variables.context], refetchType: 'active' });
    },
  });
}